import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import EditCostTiersButton from 'components/shared/highCostMeds/EditCostTiersButton';
import highCostMedListColumns from 'components/shared/highCostMeds/highCostMedListColumns';
import {
  medicationsFoundLabel,
  RxnormCounts,
  RxnormFilter,
  RxnormFilterCheckboxes,
  RxnormRename,
  rxnormRowStyle,
  rxnormStatesFor,
} from 'components/shared/highCostMeds/rxnormStatus';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import usePagination from 'hooks/usePagination';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { PERMISSIONS } from 'models/Profile';
import {
  highCostMedicationListItemQueryKeys,
  indexHighCostMedicationListItems,
  updateHighCostMedicationListItem,
} from 'services/api/highCostMedicationListItems';
import { highCostMedicationTierQueryKeys, indexHighCostMedicationTiers } from 'services/api/highCostMedicationTiers';
import { validationErrorMessage } from 'services/api/validationErrors';
import { serializeSort } from 'stores/helpers';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import EditCostTiersModal from './EditCostTiersModal';
import HighCostMedEntryModal from './HighCostMedEntryModal';
import HighCostMedsEmptyState from './HighCostMedsEmptyState';
import HighCostMedsRowMenu from './HighCostMedsRowMenu';
import RemoveHighCostMedEntryModal from './RemoveHighCostMedEntryModal';

// Matches the API default (Date Added desc) so the first load mirrors the mock.
const DEFAULT_SORT: SortingState = [{ id: 'createdAt', desc: true }];

type ActiveModal =
  | { type: 'add' }
  | { type: 'edit'; item: HighCostMedicationListItem }
  | { type: 'remove'; item: HighCostMedicationListItem }
  | { type: 'tiers' };

function HighCostMedsPage() {
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState(''); // immediate input value
  const [search, setSearch] = useState(''); // committed (debounced) search term
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const [rxnormFilters, setRxnormFilters] = useState<RxnormFilter[]>([]);

  // Debounce the commit so search and its relevance sort are applied together — the query never
  // sees a new search with a stale sort. Mirrors DenialListPage.
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setSort(value ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
      }, 500),
    []
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
      rxnormState: rxnormStatesFor(rxnormFilters),
    }),
    [pagination.paging, sort, search, rxnormFilters]
  );

  // Reset to the first page whenever the committed search or the filter changes.
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rxnormFilters]);

  const { data, isLoading } = useQuery({
    queryKey: highCostMedicationListItemQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexHighCostMedicationListItems(queryParams, signal),
    placeholderData: keepPreviousData,
  });

  // The cost tier key. Fetched separately because customers never see the list entity that carries
  // it, and it is the same handful of rows on every page. See design 003.
  const { data: tierData } = useQuery({
    queryKey: highCostMedicationTierQueryKeys.index(),
    queryFn: ({ signal }) => indexHighCostMedicationTiers(signal),
  });

  const tiers = tierData?.data ?? [];

  const { profile } = useProfile();
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();
  const { mutate: rename } = useMutateWithToast({
    mutationFn: updateHighCostMedicationListItem,
    errorToast: validationErrorMessage,
  });
  const onRename: RxnormRename = (item, field, name) =>
    rename(
      { id: item.id, [field]: name },
      {
        onSuccess: () => {
          invalidateQueries({ highCostMedicationListItems: true });
          addToast({ text: `Renamed to ${name}` });
        },
      }
    );
  const canEdit = profile.has(PERMISSIONS.highCostMedicationListItemEdit);

  // RowMenu is passed unconditionally: it returns null for a user who can neither edit nor remove,
  // so a view-only user gets no kebab.
  const columns = useMemo(
    () =>
      highCostMedListColumns({
        tiers,
        onEdit: (item) => setActiveModal({ type: 'edit', item }),
        onRemove: (item) => setActiveModal({ type: 'remove', item }),
        RowMenu: HighCostMedsRowMenu,
        onRename: canEdit ? onRename : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tierData, canEdit]
  );

  const closeModal = () => setActiveModal(null);

  const totalRecords = data?.meta.totalRecords ?? 0;
  const rxnormStateCounts = data?.meta.rxnormStateCounts;
  const showEmptyState = !isLoading && totalRecords === 0 && !search && rxnormFilters.length === 0;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>High Cost Medication List</Title>
            <SearchContainer>
              <Search value={searchInput} onChange={handleSearchChange} placeholder='Search medications' />
            </SearchContainer>
            <RxnormFilterCheckboxes value={rxnormFilters} onChange={setRxnormFilters} />
          </TitleGroup>
          {/* Each action carries its own gate, so a user who curates the tier key but does not
              maintain the medications sees only the link, and vice versa. A view-only user holds
              neither and gets a header with no actions, which is the correct read-only page. */}
          <HeaderActions>
            <AccessGuard accessor={PERMISSIONS.highCostMedicationListEdit}>
              {/* Disabled until the tier key has actually arrived. `tiers` cannot distinguish an
                  unresolved or failed query from a list whose medications genuinely carry no tiers, so
                  opening the form early would assert the No Cost Tiers state for an organization that
                  has them — and permanently so if the request failed rather than merely being slow. */}
              <EditCostTiersButton onClick={() => setActiveModal({ type: 'tiers' })} disabled={!tierData} />
            </AccessGuard>
            <AccessGuard accessor={PERMISSIONS.highCostMedicationListItemCreate}>
              <Button variant='primary' onClick={() => setActiveModal({ type: 'add' })}>
                <PlusIcon width={16} height={16} />
                Add New Medication
              </Button>
            </AccessGuard>
          </HeaderActions>
        </HeaderRow>
      </Page.Header>

      {!showEmptyState && (
        <Page.SubHeader>
          <CountRow>
            <BodySmall>{medicationsFoundLabel(totalRecords, rxnormStateCounts)}</BodySmall>
            <RxnormCounts counts={rxnormStateCounts} />
          </CountRow>
        </Page.SubHeader>
      )}

      <Page.Content>
        {showEmptyState ? (
          <HighCostMedsEmptyState onAdd={() => setActiveModal({ type: 'add' })} />
        ) : (
          <ControlledTable<HighCostMedicationListItem>
            data={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            pageCount={data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            rowStyle={rxnormRowStyle}
            // Sorting is applied by the API and it has no fallback ORDER BY, so an unsorted list
            // paginates non-deterministically — rows repeat across pages and others never appear.
            // Keep every header click on a direction rather than cycling back to no sort.
            enableSortingRemoval={false}
          />
        )}
      </Page.Content>

      {activeModal?.type === 'add' && (
        <HighCostMedEntryModal
          tiers={tiers}
          onClose={closeModal}
          // A medication added while a search or filter is active would otherwise land behind it,
          // leaving a table that doesn't show it under an "added successfully" toast.
          onSaved={() => {
            handleSearchChange('');
            applySearch.flush();
            setRxnormFilters([]);
          }}
        />
      )}
      {activeModal?.type === 'edit' && (
        <HighCostMedEntryModal highCostMedicationListItem={activeModal.item} tiers={tiers} onClose={closeModal} />
      )}
      {activeModal?.type === 'remove' && (
        <RemoveHighCostMedEntryModal highCostMedicationListItem={activeModal.item} onClose={closeModal} />
      )}
      {activeModal?.type === 'tiers' && <EditCostTiersModal tiers={tiers} onClose={closeModal} />}
    </Page>
  );
}

export default HighCostMedsPage;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const Title = styled(H2)`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;
